import * as React from "react"
import { connect } from "react-redux"
import { GlobalState } from "../../../../store"

import AuthBasePage from "../../AuthBasePage"
import {
    Action,
    ActionAnchor,
    ActionType,
} from "@tenforce/toolbox-button-push-react"
import { Checkbox } from "@tenforce/toolbox-selector-react"
import { FieldItems, Input } from "@tenforce/toolbox-field-react"

import { mapDispatchToProps, DispatchProps } from "../../actions"
import env from "../../../../env"
import { isBrowserOutdated } from "../../../../util/UserAgent"
import { clearSearchQuery } from "../../../search/persist"
import { Symbols } from "@tenforce/toolbox-fontmap"
import { ILoginPage } from "../../index"
import { ITenForceWindow } from "../../../../global"

declare let window: ITenForceWindow

const applicationPath = env.applicationPath
const sadminIsProtected = env.sadminIsProtected
const sadminUsername = env.sadminUsername?.trim().toLowerCase()
const sadminEmail = env.sadminEmail?.trim().toLowerCase()
const serverErrorMessage = env.serverErrorMessage
const shouldShowSamlLoginUrl = env.shouldShowSamlLoginUrl

type StateProps = ILoginPage

type Props = StateProps & DispatchProps

interface State {
    username: string
    password: string
    previousPassword: string
    remember: boolean
    outdatedErrorMessage: string
    serverErrorMessage: string
}

export class LoginPage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            username: this.props.username,
            password: this.props.password,
            previousPassword: this.props.password,
            remember: this.props.remember,
            outdatedErrorMessage: "",
            serverErrorMessage: "",
        }
    }

    componentDidMount(): void {
        clearSearchQuery()
        this.setup()
    }

    static getDerivedStateFromProps(props: Props, state: State): State {
        return {
            ...state,
            password:
                props.password !== state.previousPassword
                    ? props.password
                    : state.password,
            previousPassword:
                props.password !== state.previousPassword
                    ? props.password
                    : state.previousPassword,
        }
    }

    render(): JSX.Element {
        const { username, password, remember } = this.state
        const { isLoading } = this.props
        const queryParameters = new URLSearchParams(window.location.search)
        const redirect = queryParameters.get("redirectUrl")
        const redirectQueryParam = !!redirect ? `?redirectUrl=${redirect}` : ""
        const samlLoginHref = `${applicationPath}/login/saml${redirectQueryParam}`

        return (
            <AuthBasePage showRecoverPasswordUrl={true} isLoading={isLoading}>
                <form id="loginForm" onSubmit={this.onLogin}>
                    {shouldShowSamlLoginUrl && (
                        <div
                            id="samlLoginLink"
                            className="is-inverted tw-flex tw-justify-end tw-pb-8 tw-pr-4"
                        >
                            {!isLoading && (
                                <ActionAnchor href={samlLoginHref}>
                                    {Symbols.Password} Login through my
                                    organisation
                                </ActionAnchor>
                            )}
                            {isLoading && (
                                <span className="toolbox-action">
                                    {Symbols.Password} Login through my
                                    organisation
                                </span>
                            )}
                        </div>
                    )}
                    {this.renderErrors()}
                    <FieldItems isInverted={true} label="Username">
                        <Input
                            id="userName"
                            name="userName"
                            autoComplete="username"
                            required={true}
                            autoFocus={true}
                            placeholder="Enter your username or email"
                            value={username}
                            onChangedValue={(username): void =>
                                this.setState({ username })
                            }
                            disabled={isLoading}
                        />
                    </FieldItems>

                    <FieldItems isInverted={true} label="Password">
                        <Input
                            id="password"
                            name="password"
                            autoComplete="current-password"
                            placeholder="Enter your password"
                            value={password}
                            type="password"
                            onChangedValue={(password): void =>
                                this.setState({ password })
                            }
                            disabled={isLoading}
                        />
                    </FieldItems>

                    <FieldItems isInverted={true}>
                        <Checkbox
                            id="keepMeLoggedIn"
                            isRequired={true}
                            isDisabled={isLoading}
                            isChecked={remember}
                            onChecked={(
                                event: React.MouseEvent<HTMLDivElement>,
                            ): void => {
                                this.updateRememberMe(event, true)
                            }}
                            onUnchecked={(
                                event: React.MouseEvent<HTMLDivElement>,
                            ): void => {
                                this.updateRememberMe(event, false)
                            }}
                        />
                        <span className="is-inverted">
                            &nbsp;Keep me logged in for 2 weeks (uses a cookie)
                        </span>
                    </FieldItems>

                    <div>
                        <Action
                            id="SignIn"
                            name="SignIn"
                            type="submit"
                            actionType={ActionType.button}
                            isLabel={true}
                            disabled={isLoading}
                        >
                            Sign In
                        </Action>
                    </div>
                </form>
            </AuthBasePage>
        )
    }

    renderError = (id: string, message: string): JSX.Element => {
        return (
            <>
                {message && (
                    <div className="theme-bubble bg-cadmium-red text-white tw-px-3 tw-py-1 tw-w-fit">
                        <p id={id}>{message}</p>
                    </div>
                )}
            </>
        )
    }

    renderErrors = (): JSX.Element => {
        const { serverErrorMessage, outdatedErrorMessage } = this.state
        const {
            incompatibleErrorMessage,
            loginErrorMessage,
            underMaintenanceErrorMessage,
        } = this.props

        const showLoginError =
            !!loginErrorMessage &&
            ![
                serverErrorMessage,
                incompatibleErrorMessage,
                outdatedErrorMessage,
                underMaintenanceErrorMessage,
            ].includes(loginErrorMessage)

        return (
            <>
                {this.renderError("serverMessage", serverErrorMessage)}
                {this.renderError(
                    "incompatibleMessage",
                    incompatibleErrorMessage,
                )}
                {this.renderError(
                    "maintenanceMessage",
                    underMaintenanceErrorMessage,
                )}
                {this.renderError("outdatedMessage", outdatedErrorMessage)}
                {showLoginError &&
                    this.renderError("errorMessage", loginErrorMessage)}
            </>
        )
    }

    setup = (): void => {
        if (isBrowserOutdated()) {
            this.setState({
                outdatedErrorMessage:
                    "Your browser is outdated. Consider updating it or using a different one.",
            })
        }
        if (serverErrorMessage) {
            this.setState({ serverErrorMessage: serverErrorMessage })
            // No point checking the other status endpoints if the server already failed on setup
            return
        }
        this.props.requestMaintenance()
        this.props.requestDatabaseVersion()
    }

    onLogin = (event: React.FormEvent<HTMLElement>): void => {
        const { username, password, remember } = this.state
        const { requestLogin } = this.props

        event.preventDefault()

        // If the sadmin is protected, we should redirect anyone who tries to log in as him to a different login page.
        if (
            sadminIsProtected &&
            [sadminUsername, sadminEmail].includes(
                username.trim().toLowerCase(),
            )
        ) {
            return window.location.replace(applicationPath + "/account/salogin")
        }

        this.setState({ previousPassword: password })

        requestLogin(username, password, remember)
    }

    updateRememberMe = (event: React.MouseEvent, remember: boolean): void => {
        this.setState({ remember })
        // We focus the other button of the selector to keep its behavior in line with a normal checkbox
        document
            .querySelectorAll(
                `#keepMeLoggedIn > [name="${
                    remember ? "is-unchecked" : "is-checked"
                }"]`,
            )
            .forEach((elem: HTMLButtonElement) => {
                elem.focus()
            })
        event.preventDefault()
    }
}

const mapStateToProps = (state: GlobalState): StateProps => state.auth.loginPage

export default connect(mapStateToProps, mapDispatchToProps)(LoginPage)
